import { createContext, useContext, useState, useCallback, useRef } from "react";
import { AlertTriangle, Info } from "lucide-react";
import Modal from "./Modal";

/**
 * ConfirmModal — drop-in styled replacement for window.confirm.
 *
 * Two ways to use it:
 *
 * 1. Imperative (recommended) — wrap your app with <ConfirmProvider/> then
 *    call `useConfirm()` and `await confirm({ title, message })` from any
 *    handler. Returns true/false:
 *
 *       const confirm = useConfirm();
 *       const ok = await confirm({
 *         title: "Delete order?",
 *         message: "This cannot be undone.",
 *         confirmLabel: "Delete",
 *         danger: true,
 *       });
 *       if (!ok) return;
 *
 * 2. Render the <ConfirmModal/> directly with open/onConfirm/onCancel.
 */

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState(opts || {});
    });
  }, []);

  const settle = (val) => {
    setState(null);
    const r = resolverRef.current;
    resolverRef.current = null;
    r?.(val);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <ConfirmModal
          {...state}
          open
          onConfirm={() => settle(true)}
          onCancel={() => settle(false)}
        />
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fallback so the hook is safe to call before the provider mounts
    // (e.g. during HMR). Falls back to native confirm.
    return (opts) => Promise.resolve(window.confirm(`${opts?.title || ""}\n\n${opts?.message || ""}`.trim()));
  }
  return ctx;
}

/**
 * <ConfirmModal/> presentational component.
 * Render this yourself if you don't want the imperative pattern.
 */
export default function ConfirmModal({
  open = true,
  title = "Are you sure?",
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger = false,
  busy = false,
  onConfirm,
  onCancel,
}) {
  if (!open) return null;
  const Icon = danger ? AlertTriangle : Info;
  return (
    <Modal
      title={title}
      icon={<Icon className={`w-4 h-4 ${danger ? "text-red-600" : "text-brand-600"}`} />}
      onClose={busy ? undefined : onCancel}
      size="md"
    >
      <div className="px-6 py-5 space-y-5" data-testid="confirm-modal">
        {message && (
          <div className="text-sm text-zinc-700 whitespace-pre-line leading-relaxed">{message}</div>
        )}
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid="confirm-cancel-btn"
            className="es-btn-ghost"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            data-testid="confirm-ok-btn"
            className={[
              "inline-flex items-center gap-2 h-10 px-4 text-sm font-semibold rounded-lg transition-colors disabled:opacity-60",
              danger
                ? "bg-red-600 text-white hover:bg-red-700"
                : "bg-brand-600 text-white hover:bg-brand-700",
            ].join(" ")}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}
